import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Input, InputNumber, Popconfirm, Spin, Table, Tooltip, Typography } from "antd";
import type { ButtonProps, TableColumnsType } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { deleteNoteAttribute, getNoteAttributes, updateNoteAttribute } from "@store/actions/notes.actions";
import type { NoteAttribute, NotesState } from "@store/reducers/notes.reducer";
import { readNoteApiError } from "@utils/noteApiError.mjs";
import {
    ATTRIBUTES_DESCRIPTION,
    attributeDraftErrors,
    attributePatch,
    draftOf,
    errorSlot,
    renameConfirmText,
    sortAttributesByName,
    usageText,
} from "@utils/noteAttributes.mjs";
import type { AttributeDraft } from "@utils/noteAttributes.mjs";
import { formatAttributeRange } from "@utils/notesForm.mjs";
import { NoteAttributeCreateForm } from "./NoteAttributeCreateForm";

const CONFIRM_PROPS = { "data-attr": "notes.attributes.confirm" } as ButtonProps;
const DELETE_CONFIRM_PROPS = { danger: true, "data-attr": "notes.attributes.confirm" } as ButtonProps;
const CANCEL_PROPS = { "data-attr": "notes.attributes.cancel" } as ButtonProps;

type RowServerErrors = { name?: string; range?: string };

// One row at a time is in edit mode. The other rows show plain text.
type Editing = { id: number; draft: AttributeDraft };

const FieldError = ({ text }: { text?: string | null }) =>
    text ? (
        <Typography.Text type="danger" className="notes-dialog-error">
            {text}
        </Typography.Text>
    ) : null;

export const AttributeSettings = () => {
    const dispatch = useDispatch<any>();
    const attributes = useSelector((state: { notes: NotesState }) => state.notes.attributes);
    const loading = useSelector((state: { notes: NotesState }) => state.notes.loading.attributes);

    const [editing, setEditing] = useState<Editing | null>(null);
    const [serverErrors, setServerErrors] = useState<RowServerErrors>({});
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);

    // The usage counts must be fresh. The thunk shows its own errors. The `catch` only stops an unhandled rejection.
    useEffect(() => {
        dispatch(getNoteAttributes()).catch(() => {});
    }, [dispatch]);

    const sorted = useMemo(() => sortAttributesByName(attributes), [attributes]);

    const startEdit = (attribute: NoteAttribute) => {
        setEditing({ id: attribute.id, draft: draftOf(attribute) });
        setServerErrors({});
    };

    const stopEdit = () => {
        setEditing(null);
        setServerErrors({});
    };

    const edit = (patch: Partial<AttributeDraft>) => {
        setEditing((current) => current && { ...current, draft: { ...current.draft, ...patch } });
        setServerErrors({});
    };

    // An error with a `field` shows next to its input, when the row has one. The thunk shows an error that has no field.
    const showError = (e: unknown) => {
        const { message: text, field } = readNoteApiError(e);
        if (field === null) return;
        const slot = errorSlot(field);
        if (slot) setServerErrors({ [slot]: text });
        else message.error(text);
    };

    const save = async (attribute: NoteAttribute, patch: ReturnType<typeof attributePatch>) => {
        setSaving(true);
        setServerErrors({});
        try {
            await dispatch(updateNoteAttribute(attribute.id, patch));
            stopEdit();
            message.success("Attribute saved.");
        } catch (e) {
            showError(e);
        } finally {
            setSaving(false);
        }
    };

    // The thunk shows an error that has no field, such as an attribute that a note started to use.
    const remove = (attribute: NoteAttribute) =>
        dispatch(deleteNoteAttribute(attribute.id)).catch((e: unknown) => {
            const { message: text, field } = readNoteApiError(e);
            if (field !== null) message.error(text);
        });

    const renderSave = (attribute: NoteAttribute, draft: AttributeDraft) => {
        const patch = attributePatch(attribute, draft);
        const errors = attributeDraftErrors(draft);
        const question = renameConfirmText(attribute, patch);
        const button = (
            <Button
                type="primary"
                size="small"
                disabled={Object.keys(patch).length === 0 || Boolean(errors.name || errors.range)}
                loading={saving}
                onClick={question ? undefined : () => save(attribute, patch)}
                data-attr="notes.attributes.save"
            >
                Save
            </Button>
        );
        if (!question) return button;
        return (
            <Popconfirm
                title={question}
                okText="Rename"
                okButtonProps={CONFIRM_PROPS}
                cancelButtonProps={CANCEL_PROPS}
                onConfirm={() => save(attribute, patch)}
            >
                {button}
            </Popconfirm>
        );
    };

    // An attribute that a note uses cannot be deleted. Its delete button stays in the row, turned off, with the reason on hover.
    const renderDelete = (attribute: NoteAttribute) => {
        const used = attribute.usage_count > 0;
        return (
            // A disabled button gets no mouse events, so the span carries the tooltip.
            <Tooltip title={used ? `“${attribute.name}” is used by ${usageText(attribute.usage_count)}.` : null}>
                <span>
                    <Popconfirm
                        title={`Delete “${attribute.name}”?`}
                        okText="Delete"
                        okButtonProps={DELETE_CONFIRM_PROPS}
                        cancelButtonProps={CANCEL_PROPS}
                        onConfirm={() => remove(attribute)}
                    >
                        <Button
                            color="danger"
                            variant="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            aria-label={`Delete ${attribute.name}`}
                            title={used ? undefined : "Delete attribute"}
                            disabled={used}
                            data-attr="notes.attributes.delete"
                        />
                    </Popconfirm>
                </span>
            </Tooltip>
        );
    };

    const draftFor = (attribute: NoteAttribute) => (editing?.id === attribute.id ? editing.draft : null);

    const columns: TableColumnsType<NoteAttribute> = [
        {
            // No width: the name column takes the spare width.
            title: "Name",
            key: "name",
            render: (_, attribute) => {
                const draft = draftFor(attribute);
                if (!draft) return attribute.name;
                const error = attributeDraftErrors(draft).name ?? serverErrors.name;
                return (
                    <>
                        <Input
                            className="notes-full-width"
                            aria-label={`Name of ${attribute.name}`}
                            status={error ? "error" : undefined}
                            value={draft.name}
                            onChange={(e) => edit({ name: e.target.value })}
                        />
                        <FieldError text={error} />
                    </>
                );
            },
        },
        {
            title: "Range",
            key: "range",
            width: 160,
            render: (_, attribute) => {
                const draft = draftFor(attribute);
                if (!draft) return formatAttributeRange(attribute.min_value, attribute.max_value);
                const error = attributeDraftErrors(draft).range ?? serverErrors.range;
                return (
                    <>
                        <div className="notes-filter-range">
                            <InputNumber
                                className="notes-full-width"
                                aria-label={`Min of ${attribute.name}`}
                                placeholder="Min"
                                status={error ? "error" : undefined}
                                value={draft.min}
                                onChange={(min) => edit({ min })}
                            />
                            <span>–</span>
                            <InputNumber
                                className="notes-full-width"
                                aria-label={`Max of ${attribute.name}`}
                                placeholder="Max"
                                status={error ? "error" : undefined}
                                value={draft.max}
                                onChange={(max) => edit({ max })}
                            />
                        </div>
                        <FieldError text={error} />
                    </>
                );
            },
        },
        { title: "Used in", key: "usage", width: 110, render: (_, attribute) => usageText(attribute.usage_count) },
        {
            key: "actions",
            width: 150,
            render: (_, attribute) =>
                draftFor(attribute) ? (
                    <div className="notes-attr-actions">
                        {renderSave(attribute, draftFor(attribute) as AttributeDraft)}
                        <Button size="small" disabled={saving} onClick={stopEdit} data-attr="notes.attributes.cancel-edit">
                            Cancel
                        </Button>
                    </div>
                ) : (
                    <div className="notes-attr-actions">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            aria-label={`Edit ${attribute.name}`}
                            title="Edit attribute"
                            onClick={() => startEdit(attribute)}
                            data-attr="notes.attributes.edit"
                        />
                        {renderDelete(attribute)}
                    </div>
                ),
        },
    ];

    return (
        <Card
            title="Note attributes"
            extra={
                <Button disabled={creating} onClick={() => setCreating(true)} data-attr="notes.attributes.open-create">
                    + New attribute
                </Button>
            }
        >
            <Typography.Paragraph type="secondary">{ATTRIBUTES_DESCRIPTION}</Typography.Paragraph>
            {creating && (
                <NoteAttributeCreateForm
                    dataAttrPrefix="notes.attributes"
                    onCreated={() => {
                        setCreating(false);
                        message.success("Attribute created.");
                    }}
                    onCancel={() => setCreating(false)}
                />
            )}
            <Spin spinning={loading}>
                <Table<NoteAttribute>
                    className="notes-attr-table"
                    size="small"
                    pagination={false}
                    rowKey="id"
                    columns={columns}
                    dataSource={sorted}
                    // Every column has a width. A fixed layout spreads any spare width over them in proportion.
                    tableLayout="fixed"
                    scroll={{ x: 650 }}
                    locale={{ emptyText: "No attributes yet." }}
                />
            </Spin>
        </Card>
    );
};
